/**
 * Utility functions for managing body style overrides
 * Follows SRP, DRY, and KISS principles
 */

/**
 * Returns default values for PrintedReportSectionStyles
 */
export function getDefaultStyleOverride() {
  return {
    headingColor: '#000000',
    headingFontSize: 16,
    headingCase: 'regular',
    fontSizePixels: 12,
    color: '#000000',
    otherStyles: {},
  }
}

/**
 * Heading case options for the UI
 */
export const HEADING_CASE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'capitalized', label: 'Capitalized' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'Lowercase' },
]

/**
 * Validates style override data (basic validation)
 * Returns { valid: boolean, errors: string[] }
 */
export function validateStyleOverrideData(styleData) {
  const errors = []

  if (styleData?.headingFontSize && (styleData.headingFontSize < 1 || styleData.headingFontSize > 100)) {
    errors.push('Heading font size must be between 1 and 100')
  }

  if (styleData?.fontSizePixels && (styleData.fontSizePixels < 1 || styleData.fontSizePixels > 100)) {
    errors.push('Body font size must be between 1 and 100')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Merges existing style override with new partial data
 */
export function mergeStyleOverride(existing, updates) {
  return {
    ...getDefaultStyleOverride(),
    ...existing,
    ...updates,
  }
}

/**
 * Converts heading case value to CSS text-transform value
 */
export function headingCaseToCssTransform(headingCase) {
  const mapping = {
    regular: 'none',
    capitalized: 'capitalize',
    uppercase: 'uppercase',
    lowercase: 'lowercase',
  }
  return mapping[headingCase] || 'none'
}

/**
 * Parses otherStyles map into CSS string
 */
export function otherStylesToCssString(otherStyles) {
  if (!otherStyles || typeof otherStyles !== 'object') return ''

  return Object.entries(otherStyles)
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ')
}

/**
 * Parses CSS string into otherStyles map
 */
export function cssStringToOtherStyles(cssString) {
  if (!cssString || typeof cssString !== 'string') return {}

  const styles = {}
  const declarations = cssString.split(';').filter((s) => s.trim())

  declarations.forEach((declaration) => {
    const [key, value] = declaration.split(':').map((s) => s.trim())
    if (key && value) {
      styles[key] = value
    }
  })

  return styles
}
